import { townColorHex as hex, type TownPropPalette } from './townPropTemplates';
import { drawRopeSpan, drawTimberBoards } from './propDrawingPrimitives';
import { drawFishingNet, drawRopeCoil } from './maritimeDrawingPrimitives';

export type HarborBoatTemplate = 'moored_fishing_skiff' | 'furled_coastal_sloop';

function fastenings(points: readonly (readonly [number, number])[], p: TownPropPalette): string {
  return `<g data-part="hull-fastenings">${points.map(([x, y]) =>
    `<circle cx="${x}" cy="${y}" r="1.25" fill="${hex(p.iron)}" />
     <circle cx="${x - .2}" cy="${y - .25}" r=".4" fill="${hex(p.stone)}" />`).join('')}</g>`;
}

/** 184×84. A shallow open working boat, with its oars and loose gear kept inside the art frame. */
function fishingSkiff(p: TownPropPalette): string {
  return `<g data-part="fishing-skiff">
    ${drawRopeSpan({ x: 12, y: 40 }, { x: 4, y: 68 }, p, { sag: 7, thickness: 2.2 })}
    <g data-part="skiff-inner-gunwale">
      <path d="M10 39Q72 18 178 27Q148 51 65 55Q34 51 10 39Z" fill="${hex(p.woodEdge)}" />
      <path d="M21 39Q84 25 165 30Q137 46 65 48Q42 47 21 39Z" fill="${hex(p.iron)}" />
      <path d="M31 39Q83 28 153 31" fill="none" stroke="${hex(p.stone)}" stroke-width="1.3" />
      ${drawTimberBoards({ x: 51, y: 34, width: 16, height: 13 }, p, { axis: 'horizontal', boards: 2 })}
      ${drawTimberBoards({ x: 94, y: 30, width: 16, height: 14 }, p, { axis: 'horizontal', boards: 2 })}
    </g>
    <g data-part="skiff-cargo">
      <path d="M117 15L144 12L154 21V38L117 41Z" fill="${hex(p.shade)}" />
      ${drawTimberBoards({ x: 117, y: 21, width: 26, height: 19 }, p, { axis: 'horizontal', boards: 3 })}
      <path d="M117 15L144 12L154 21L143 25Z" fill="${hex(p.woodEdge)}" />
      <path d="M121 24L138 37 M140 18L149 36" stroke="${hex(p.wood)}" stroke-width="2" />
      <path d="M125 15L125 23 M139 14L141 24" stroke="${hex(p.iron)}" stroke-width="1.4" />
    </g>
    <g data-part="skiff-stowed-oars">
      <path d="M34 8L139 43 M47 42L148 11" stroke="${hex(p.wood)}" stroke-width="4" stroke-linecap="round" />
      <path d="M34 8L134 41 M50 41L148 11" stroke="${hex(p.woodEdge)}" stroke-width="1.6" stroke-linecap="round" />
      <path d="M131 36L143 38Q153 44 145 48L132 43Z M139 10L154 5Q161 7 155 13L143 16Z" fill="${hex(p.woodEdge)}" />
      <path d="M139 40L147 44 M146 12L155 8" stroke="${hex(p.wood)}" stroke-width="1.2" />
    </g>
    <g data-part="skiff-deck-coil">${drawRopeCoil({ x: 72, y: 24, width: 24, height: 15 }, p, { turns: 3 })}</g>
    <g data-part="skiff-curved-hull">
      <path d="M8 40Q66 54 170 35L179 26Q173 60 144 75Q124 84 69 84Q28 81 8 40Z" fill="${hex(p.wood)}" />
      <path d="M12 43Q83 58 172 35L167 47Q104 66 24 55Z" fill="${hex(p.primary)}" />
      <path d="M27 61Q88 81 157 59Q140 81 111 83L69 84Q43 81 27 61Z" fill="${hex(p.shade)}" />
      <path d="M20 50Q78 68 165 46 M30 63Q89 80 155 59 M44 74Q93 84 142 71"
        fill="none" stroke="${hex(p.woodEdge)}" stroke-width="1.3" />
      <path d="M8 39Q74 56 171 34L179 26" fill="none" stroke="${hex(p.woodEdge)}" stroke-width="3" stroke-linejoin="round" />
      <path d="M17 45L23 54 M165 40L162 50 M65 62L68 70 M119 73L117 79"
        stroke="${hex(p.iron)}" stroke-width="1.1" />
      <path d="M73 81H113V84H73Z" fill="${hex(p.iron)}" />
      ${fastenings([[28, 49], [54, 54], [80, 55], [107, 53], [135, 47], [161, 40]], p)}
    </g>
    <g data-part="skiff-draped-net">${drawFishingNet({ x: 27, y: 20, width: 34, height: 35 }, p, { columns: 4, rows: 5 })}</g>
    <path d="M30 19L27 53 M59 20L62 47" stroke="${hex(p.woodEdge)}" stroke-width="1.3" />
    <ellipse cx="29" cy="48" rx="3" ry="5" fill="${hex(p.glass)}" />
    <ellipse cx="60" cy="43" rx="2.5" ry="4" fill="${hex(p.glassCore)}" />
    <path d="M170 34L174 28" stroke="${hex(p.iron)}" stroke-width="3" />
  </g>`;
}

/** 208×188. Rigging frames open air around a tied, furled sail above a curved coastal hull. */
function coastalSloop(p: TownPropPalette): string {
  return `<g data-part="coastal-sloop">
    <g data-part="sloop-standing-rigging">
      ${drawRopeSpan({ x: 85, y: 18 }, { x: 28, y: 145 }, p, { sag: 0, thickness: 1.5 })}
      ${drawRopeSpan({ x: 85, y: 18 }, { x: 182, y: 141 }, p, { sag: 0, thickness: 1.6 })}
      ${drawRopeSpan({ x: 88, y: 24 }, { x: 162, y: 104 }, p, { sag: 5, thickness: 1.2 })}
    </g>
    <g data-part="sloop-mast">
      ${drawTimberBoards({ x: 81, y: 9, width: 8, height: 137 }, p, { axis: 'vertical', boards: 2 })}
      <path d="M80 8H90V14H80Z M79 132H91V145H79Z" fill="${hex(p.iron)}" />
      <path d="M85 16V94" stroke="${hex(p.woodEdge)}" stroke-width="1.2" />
      <path d="M90 14Q102 10 113 16L106 21L120 28Q99 24 90 26Z" fill="${hex(p.primary)}" />
      <path d="M91 15L95 14V25L91 26Z" fill="${hex(p.shade)}" />
    </g>
    <g data-part="sloop-furled-sail">
      <path d="M85 110L174 103" stroke="${hex(p.wood)}" stroke-width="5" stroke-linecap="round" />
      <path d="M88 99Q106 94 126 97Q146 91 165 96L172 105Q153 115 131 110Q108 116 88 109Z" fill="${hex(p.glassCore)}" />
      <path d="M91 105Q112 110 131 103Q151 109 167 101" fill="none" stroke="${hex(p.stone)}" stroke-width="2" />
      <path d="M97 100L103 108 M118 97L121 108 M143 96L146 108 M159 98L161 107" stroke="${hex(p.wood)}" stroke-width="2.6" />
      <path d="M99 97L100 113 M141 94L144 112" stroke="${hex(p.woodEdge)}" stroke-width="1" />
    </g>
    <g data-part="sloop-inner-gunwale">
      <path d="M16 145Q89 125 200 130L192 141Q90 162 16 145Z" fill="${hex(p.woodEdge)}" />
      <path d="M29 144Q104 133 188 135L177 142Q99 154 29 144Z" fill="${hex(p.iron)}" />
      <path d="M35 143Q89 134 137 135" stroke="${hex(p.stone)}" stroke-width="1.2" fill="none" />
    </g>
    <g data-part="sloop-low-cabin">
      ${drawTimberBoards({ x: 113, y: 120, width: 39, height: 24 }, p, { axis: 'horizontal', boards: 3 })}
      <path d="M108 115H150L159 124H105Z" fill="${hex(p.primary)}" />
      <path d="M108 115H150L159 124" fill="none" stroke="${hex(p.woodEdge)}" stroke-width="1.8" />
      <path d="M136 126H146V145H136Z" fill="${hex(p.iron)}" />
      <circle cx="123" cy="131" r="5" fill="${hex(p.iron)}" />
      <circle cx="123" cy="131" r="3.3" fill="${hex(p.glass)}" />
      <path d="M121 129L124 132" stroke="${hex(p.glassCore)}" stroke-width="1.1" />
    </g>
    <g data-part="sloop-deck-coil">${drawRopeCoil({ x: 46, y: 128, width: 25, height: 15 }, p, { turns: 4 })}</g>
    <g data-part="sloop-curved-hull">
      <path d="M14 147Q87 158 192 139L202 128Q198 161 176 175Q151 188 88 188Q40 187 14 147Z" fill="${hex(p.wood)}" />
      <path d="M19 150Q101 164 194 138L189 151Q108 175 30 161Z" fill="${hex(p.primary)}" />
      <path d="M39 169Q112 185 181 167Q162 186 123 188H88Q61 186 39 169Z" fill="${hex(p.shade)}" />
      <path d="M26 158Q104 175 190 151 M40 170Q109 186 179 165 M62 181Q119 191 163 176"
        fill="none" stroke="${hex(p.woodEdge)}" stroke-width="1.4" />
      <path d="M15 146Q90 161 192 138L201 129" fill="none" stroke="${hex(p.woodEdge)}" stroke-width="3" stroke-linejoin="round" />
      <path d="M97 185H139V188H97Z" fill="${hex(p.iron)}" />
      ${fastenings([[36, 153], [61, 157], [89, 158], [117, 157], [145, 151], [172, 145], [193, 137]], p)}
    </g>
    <g data-part="sloop-fenders">
      <path d="M57 151V158 M169 141V150" stroke="${hex(p.woodEdge)}" stroke-width="1.5" />
      <ellipse cx="57" cy="163" rx="4.5" ry="8" fill="${hex(p.iron)}" />
      <ellipse cx="57" cy="163" rx="2.2" ry="5" fill="${hex(p.woodEdge)}" />
      <ellipse cx="169" cy="155" rx="4" ry="7" fill="${hex(p.iron)}" />
      <ellipse cx="169" cy="155" rx="1.8" ry="4" fill="${hex(p.woodEdge)}" />
    </g>
    <g data-part="sloop-mooring-line">
      ${drawRopeSpan({ x: 198, y: 140 }, { x: 203, y: 178 }, p, { sag: 4, thickness: 1.8 })}
      <path d="M194 141L200 137" stroke="${hex(p.iron)}" stroke-width="3" />
    </g>
  </g>`;
}

/** Decorative, static boat art only; map data owns all ground, water and travel behavior. */
export function buildHarborBoatBody(template: HarborBoatTemplate, p: TownPropPalette): string {
  switch (template) {
  case 'moored_fishing_skiff': return fishingSkiff(p);
  case 'furled_coastal_sloop': return coastalSloop(p);
  }
}
